import { defineCopy } from '@floorfile/i18n'

/**
 * The andon page's catalogue (localization plan Phase 5a).
 *
 * ## `type` and `category` are keyed by the CONTRACT enum, not derived from it
 *
 * The page used to render these as `t.charAt(0) + t.slice(1).toLowerCase()` —
 * `BREAKDOWN` → `Breakdown`. That is not a formatting choice, it is a
 * translation baked into a string expression: it produces an English word, and
 * it produces it by imposing English orthography (initial capital, lowercase
 * remainder) on every language the app will ever ship. French wants `Panne`,
 * German capitalises the whole noun mid-sentence, and neither is reachable
 * from `.toLowerCase()`.
 *
 * Keying on the enum value also means an added `DowntimeType` fails the build
 * here — a `Record<DowntimeType, string>` is checked — rather than silently
 * rendering a screaming-caps constant on the floor.
 *
 * ## `stats.amount` takes the number as a PLACEHOLDER rather than prefixing `$`
 *
 * The page had `${f.number(total)}` — symbol, then digits. That is an English
 * convention: fr-CA writes `1 234 $`, with the sign after the number and a
 * space before it. A bare `'$'` key could only ever be concatenated on the
 * wrong side, so the whole amount is one message and the translator controls
 * the order.
 *
 * It is not `formatCurrency` for the reason recorded on that function: there is
 * no `company_settings.currency` column, so naming a currency here would be
 * inventing a company-wide fact. Grouped digits plus a symbol is the honest
 * rendering until the setting exists.
 */
export const downtimeCopy = defineCopy('downtime', {
  offlineAction: { report: 'Report downtime', resolve: 'Resolve downtime' },
  page: {
    title: 'Downtime',
    description: 'When a machine stops, the right people know in seconds.',
    report: '⬤ Report downtime',
  },

  /**
   * The reporting-period controls (audit 08-12 C3).
   *
   * The page had one filter — a `?period=month` chip a dashboard KPI set — and
   * the headline cards ignored even that: they called an unfiltered stats
   * endpoint, so "Total downtime" was every event the site had ever recorded,
   * sitting in 24-point type above a table showing one month.
   */
  period: {
    label: 'Reporting period',
    /** Quick choices. Ids stay English — they are the `?period=` slug. */
    today: 'Today',
    days7: 'Last 7 days',
    days30: 'Last 30 days',
    /** The home KPI's deep link (`?period=month`) — offered here so the scope
     * it opens with can be seen and reproduced (FF-AUDIT-009). */
    month: 'This month',
    days90: 'Last 90 days',
    ytd: 'Year to date',
    all: 'All time',
    custom: 'Custom range',
    from: 'From',
    to: 'To',
    /** Shown when the typed range cannot be read as a period. */
    invalidRange: 'The end date is before the start date.',
    /** Says out loud what the period does and does not touch. */
    appliesTo: 'The period applies to history and the totals above it. Active outages are always shown.',
    typeLabel: 'Type',
    categoryLabel: 'Cause',
    anyType: 'Any type',
    anyCategory: 'Any cause',
    plannedLabel: 'Planned',
    plannedAny: 'Planned and unplanned',
    plannedOnly: 'Planned only',
    unplannedOnly: 'Unplanned only',
  },

  stats: {
    /** FF-AUDIT-20260921-210633-035: which window the three totals cover. */
    scope: 'Showing {period}',
    events: 'Events',
    totalDowntime: 'Total downtime',
    totalCost: 'Total cost',
    /** The compact duration on the stats card. */
    hoursMinutes: '{hours}h {minutes}m',
    /** Symbol position is the translator's, not ours — see the header. */
    amount: '${amount}',
  },

  active: {
    heading: 'Active now',
    /** C3: the live board is deliberately outside the reporting period. */
    alwaysShown: 'Always shown, whatever period is selected below.',
    planned: 'Planned',
    resolve: 'Resolve',
    emptyTitle: 'Nothing is down',
    emptyDescription: 'All machines running — the board stays green.',
    /** The bounded-board notice (audit H-08). */
    truncated: 'Showing first {shown} of {total} active events — resolve some to see the rest.',
    unknownEquipment: 'Equipment',
  },

  history: {
    heading: 'History',
    /** The equipment hub's drill-through scope, as a removable chip. */
    scopeEquipment: 'Equipment: {name}',
    region: 'Downtime events',
    emptyTitle: 'No resolved events yet',
    /** `{n} min` in the duration column. */
    minutes: '{minutes} min',
    column: {
      equipment: 'Equipment',
      reason: 'Reason',
      type: 'Type',
      started: 'Started',
      duration: 'Duration',
      rootCause: 'Root cause',
      /** Owner decision 2026-09-19: the event's total cost — cost roles only. */
      cost: 'Cost',
    },
    /** Lowercase: these are read inside "Sorted by {field}, ascending". */
    sortField: {
      type: 'type',
      status: 'status',
      startedAt: 'started',
      endedAt: 'ended',
      durationMinutes: 'duration',
    },
  },

  report: {
    title: 'Report downtime',
    equipment: 'Equipment',
    equipmentPlaceholder: 'Which machine?*…',
    reason: 'What happened? (one line)',
    reasonPlaceholder: 'What happened? (one line)*',
    planned: 'Planned (no automatic corrective dispatch)',
    /** FF-AUDIT-20260914-210459-042: an outage found after the fact. */
    startedAt: 'Started at',
    startedAtHelp: 'Leave as is for "now"; set it earlier for an outage discovered late.',
    /** FF-AUDIT-20260914-210459-040: the server refused a duplicate. */
    alreadyOpen: 'This machine is already down since {since} ({reason}). Resolve or correct that event instead.',
    startInFuture: 'The start time cannot be in the future.',
    cancel: 'Cancel',
    submit: 'Report',
    failed: 'Could not report.',
    /**
     * FF-QA-088 (owner decision 2026-09-27): the machine already has open
     * repair work. The reporter says whether this outage is one of those
     * problems or a new one — the playbook used to decide for them, and
     * filed a burst hose under an alarm repair.
     */
    link: {
      title: 'Which work order is this for?',
      intro: 'This machine already has open repair work orders. Is this the same problem as one of them, or a new one?',
      fileUnder: 'File under this work order',
      createNew: 'Create a new work order',
      createNewHint: 'A new corrective work order is raised and dispatched to a technician.',
      /** One candidate row: its number and title. */
      row: '{number} · {title}',
      raisedToday: 'Raised today',
      raisedDaysAgo: { one: 'Raised {count} day ago', other: 'Raised {count} days ago' },
      unassigned: 'Unassigned',
      back: 'Back',
      submit: 'Report',
    },
  },

  /** FF-AUDIT-20260914-210459-042: the row opens; start/end can be corrected with a reason. */
  /** F6: the check or work-order step whose answer opened the outage. */
  source: {
    check: 'From check {ref}',
    step: 'From {ref} step {step}',
    /** F24B: an EQUIPMENT_STATE telemetry rule opened this outage; `ref` is
     * the rule's name (snapshotted, so a later rename or deletion cannot
     * change what an already-open event says caused it). */
    rule: 'From rule {name}',
  },
  detail: {
    source: 'Raised from',
    title: 'Downtime: {reason}',
    open: 'Open details',
    equipment: 'Equipment',
    openEquipment: 'Open the machine',
    openWorkOrders: 'Work orders for this machine',
    started: 'Started',
    ended: 'Ended',
    stillOpen: 'Still open',
    duration: 'Duration',
    reportedBy: 'Reported by',
    rootCause: 'Root cause',
    actionTaken: 'Action taken',
    workOrder: 'Work order',
    /** Owner decision 2026-09-19: the breakdown, for the company's cost roles. */
    cost: {
      productionLoss: 'Production loss',
      labor: 'Labour',
      parts: 'Parts',
      additional: 'Other costs',
      total: 'Total cost',
      rateSource: 'Production loss at {rate} per hour.',
      repairSource: 'Labour and parts from work order {number}.',
    },
    correctHeading: 'Correct the times',
    correctHelp: 'Corrections keep the original on record. A reason is required.',
    endOnResolve: 'An open outage ends through Resolve.',
    correctionReason: 'Reason for the correction',
    correctionReasonPlaceholder: 'Why the recorded time was wrong…',
    save: 'Save correction',
    saving: 'Saving…',
    saved: 'Correction saved.',
    failed: 'Could not save the correction.',
    close: 'Close',
    /** Keyed on the server's code. */
    error: {
      DOWNTIME_RANGE_INVALID: 'The end time must be after the start time.',
      DOWNTIME_START_IN_FUTURE: 'The start time cannot be in the future.',
      DOWNTIME_OVERLAP: 'This range overlaps another downtime event on the same machine (started {since}).',
      EDIT_REASON_REQUIRED: 'Give a reason for the correction.',
    },
  },

  resolve: {
    /** The dialog title carries the event's own reason text. */
    title: 'Resolve: {reason}',
    category: 'Downtime category',
    rootCause: 'Root cause',
    rootCausePlaceholder: 'Root cause…',
    /** FF-AUDIT-041: an unplanned stop must say why or what was done. */
    rootCauseRequiredPlaceholder: 'Root cause (or the action taken, below)*…',
    costNegative: 'A cost cannot be negative.',
    resolutionEmpty: 'To resolve an unplanned stop, record the root cause or the action taken.',
    insertSuggestion: 'Insert into root cause',
    actionTaken: 'Action taken',
    actionTakenPlaceholder: 'Action taken…',
    laborCost: 'Labor $',
    partsCost: 'Parts $',
    /** Owner decision 2026-09-19: the four-part cost. */
    costs: 'Costs',
    productionLossFromRate: 'Production loss is worked out from this machine’s rate ({rate} per hour) and the stop’s duration.',
    productionLossNoRate: 'This machine has no hourly downtime rate, so no production loss is added.',
    repairFromWorkOrder: 'Labour and parts come from work order {number} — its timed hours × wage and the parts it used.',
    repairNoWorkOrder: 'No work order is linked; enter labour and parts here if you know them.',
    /** Owner decision 2026-09-28: a stop filed under an order another stop raised. */
    repairCountedOnFirstStop: 'Filed under work order {number}. Its labour and parts are counted once, on the stop that raised it — this stop adds only its own lost production.',
    repairCountedOnAnotherOrder: 'Its labour and parts are counted on another work order — this stop adds only its own lost production.',
    additionalCost: 'Other costs $',
    additionalCostNote: 'What the other costs were',
    additionalCostNotePlaceholder: 'e.g. Contractor call-out, rented lift…',
    restoresStatus: "Resolving restores the machine's status if reporting changed it.",
    cancel: 'Cancel',
    submit: 'Resolve',
    failed: 'Could not resolve.',
  },

  /**
   * `DowntimeStatus`, added for the equipment page's Downtime tab (chunk
   * 5a-3), which rendered `{event.status}` raw — `ACTIVE` and `RESOLVED` in
   * screaming caps. The andon page itself only ever branches on the value, so
   * it never needed a label; the labels live here rather than in
   * `equipment/copy.ts` because the enum is downtime's, and two catalogues
   * owning the same two words is the duplication this migration removes.
   *
   * English changes here — `ACTIVE` → `Active`, `RESOLVED` → `Resolved` — for
   * the same reason `BREAKDOWN` → `Breakdown` did in 5a-1: a raw enum reaching
   * the floor is a defect, not a wording choice. The e2e suite asserts these
   * values on API payloads, never as page text, so no locator moves.
   */
  status: {
    ACTIVE: 'Active',
    RESOLVED: 'Resolved',
  },

  type: {
    BREAKDOWN: 'Breakdown',
    MAINTENANCE: 'Maintenance',
    CHANGEOVER: 'Changeover',
    ADJUSTMENT: 'Adjustment',
    IDLE: 'Idle',
    OTHER: 'Other',
  },

  category: {
    MECHANICAL: 'Mechanical',
    ELECTRICAL: 'Electrical',
    HYDRAULIC: 'Hydraulic',
    PNEUMATIC: 'Pneumatic',
    SOFTWARE: 'Software',
    OPERATOR: 'Operator',
    MATERIAL: 'Material',
    OTHER: 'Other',
  },
})

export type DowntimeCopy = typeof downtimeCopy.en
